/**
 * مركز الاتصال — الكتالوج المشترك.
 *
 * المكالمة هنا كيانٌ مستقل عن النشاط: النشاط سطرٌ في خطّ زمني، والمكالمة
 * لها عمرٌ (رنّت، أُجيبت، انتهت) ونتيجةٌ وتقييمُ جودة ومزوّدُ نقلٍ قد
 * يتبدّل. وعند انتهائها يكتب الخادم نشاطًا واحدًا في الخطّ الزمني —
 * فلا يسجّل الموظف مكالمته مرتين.
 *
 * ## حدود النقل الصوتي — بصراحة
 *
 * إجراء المكالمة عبر الإنترنت (WebRTC/SIP) يتطلب حسابَ مزوّد اتصالات
 * ورقمًا مستأجرًا. الطبقة هنا تجريدٌ يقبل أي مزوّد لاحقًا، وما دام لا
 * مزوّد مربوطًا فالاتصال يخرج من جهاز الموظف (`tel:`) **ويُسجَّل** في
 * النظام: البداية والمدة والنتيجة والملاحظات — وهو ما يعطي التقارير
 * قيمتها اليوم قبل ربط المزوّد.
 */

import type { Labelled } from './common.js';

/* ==========================================================================
   حالة المكالمة
   ========================================================================== */

export const CALL_STATUSES = [
  'IN_PROGRESS',
  'COMPLETED',
  'NO_ANSWER',
  'MISSED',
  'CANCELLED',
] as const;

export type CallStatus = (typeof CALL_STATUSES)[number];

export const CALL_STATUS_DEFS: readonly Labelled<CallStatus>[] = [
  { key: 'IN_PROGRESS', label: 'جارية', kind: 'info' },
  { key: 'COMPLETED', label: 'تمّت', kind: 'success' },
  { key: 'NO_ANSWER', label: 'لم يُجَب', kind: 'warning' },
  /* الفائتة واردةٌ لم يلحقها أحد — وهي ما يستحق إشعارًا. */
  { key: 'MISSED', label: 'فائتة', kind: 'danger' },
  { key: 'CANCELLED', label: 'أُلغيت', kind: 'neutral' },
];

export const CALL_STATUS_MAP = Object.fromEntries(
  CALL_STATUS_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<CallStatus, Labelled<CallStatus>>>;

/* ==========================================================================
   نتائج المكالمات — الافتراضية
   ========================================================================== */

/**
 * الكتالوج الابتدائي لنتائج المكالمات.
 *
 * الإدارة تحرّره من الإعدادات — تضيف وتوقف وتعيد الترتيب — وهذه البذرة
 * تُزرع عند أول استعمال حتى لا يقف الموظف أمام قائمة فارغة.
 *
 * `suggestsFollowUp` تفتح نافذة «متى نتابع؟» بعد اختيار النتيجة —
 * فالعميل الذي قال «كلّمني بعد ثلاثة أيام» يصير مهمةً لا ذكرى.
 */
export interface DispositionSeed {
  key: string;
  label: string;
  kind: 'success' | 'info' | 'warning' | 'danger' | 'neutral';
  suggestsFollowUp: boolean;
  /** أين تُعرض: بابُ المبيعات أم بابُ التوظيف. */
  scope?: DispositionScope;
}

/**
 * نطاقُ النتيجة.
 *
 * الكتالوجُ كان واحدًا ولغتُه لغةُ بيع. ومن أنهى مكالمةَ فرزٍ مع مرشّحٍ
 * أُجبر على اختيار «مهتم» أو «أُغلقت صفقة» — فيخرج سجلٌّ يقول إنّ الشركة
 * أغلقت صفقةً مع باحثٍ عن عمل. والقائمةُ الثانية ليست ترفًا: كلماتُ
 * التوظيف غيرُ كلماتِ البيع لأنّ ما يُقاس مختلف.
 */
export const DISPOSITION_SCOPES = ['SALES', 'RECRUIT'] as const;
export type DispositionScope = (typeof DISPOSITION_SCOPES)[number];

export const DEFAULT_DISPOSITIONS: readonly DispositionSeed[] = [
  { key: 'interested', label: 'مهتم', kind: 'success', suggestsFollowUp: true },
  { key: 'meeting-booked', label: 'حُجز اجتماع', kind: 'success', suggestsFollowUp: false },
  { key: 'sale-closed', label: 'أُغلقت صفقة', kind: 'success', suggestsFollowUp: false },
  { key: 'follow-up', label: 'يحتاج متابعة', kind: 'info', suggestsFollowUp: true },
  { key: 'callback', label: 'طلب معاودة الاتصال', kind: 'info', suggestsFollowUp: true },
  { key: 'no-answer', label: 'لم يردّ', kind: 'warning', suggestsFollowUp: true },
  { key: 'busy', label: 'مشغول', kind: 'warning', suggestsFollowUp: true },
  { key: 'not-interested', label: 'غير مهتم', kind: 'danger', suggestsFollowUp: false },
  { key: 'wrong-number', label: 'رقم خاطئ', kind: 'neutral', suggestsFollowUp: false },
  { key: 'complaint', label: 'شكوى', kind: 'danger', suggestsFollowUp: true },
  { key: 'support', label: 'طلب دعم', kind: 'info', suggestsFollowUp: true },
];

/**
 * نتائجُ مكالمة التوظيف.
 *
 * ما يُسأل عنه في مكالمة الفرز الأولى: هل يريدها، وهل الراتب يناسبه،
 * ومتى يستطيع أن يبدأ. وهي أسئلةٌ لا تصلح لها قائمةُ البيع.
 */
export const RECRUIT_DISPOSITIONS: readonly DispositionSeed[] = [
  {
    key: 'rc-interested',
    label: 'مهتم بالوظيفة',
    kind: 'success',
    suggestsFollowUp: false,
    scope: 'RECRUIT',
  },
  {
    key: 'rc-salary-gap',
    label: 'الراتب لا يناسبه',
    kind: 'danger',
    suggestsFollowUp: false,
    scope: 'RECRUIT',
  },
  {
    key: 'rc-not-available',
    label: 'غير متاح الآن',
    kind: 'warning',
    suggestsFollowUp: true,
    scope: 'RECRUIT',
  },
  {
    key: 'rc-no-answer',
    label: 'لم يردّ',
    kind: 'warning',
    suggestsFollowUp: true,
    scope: 'RECRUIT',
  },
  {
    key: 'rc-withdrew',
    label: 'سحب طلبه',
    kind: 'neutral',
    suggestsFollowUp: false,
    scope: 'RECRUIT',
  },
];

/* ==========================================================================
   حالة الموظف
   ========================================================================== */

export const AGENT_STATES = ['AVAILABLE', 'ON_CALL', 'BUSY', 'BREAK', 'OFFLINE'] as const;
export type AgentState = (typeof AGENT_STATES)[number];

export const AGENT_STATE_DEFS: readonly Labelled<AgentState>[] = [
  { key: 'AVAILABLE', label: 'متاح', kind: 'success' },
  /* تُضبط آليًّا مع بدء المكالمة وانتهائها — لا يقلبها الموظف بيده. */
  { key: 'ON_CALL', label: 'في مكالمة', kind: 'info' },
  { key: 'BUSY', label: 'مشغول', kind: 'warning' },
  { key: 'BREAK', label: 'استراحة', kind: 'neutral' },
  { key: 'OFFLINE', label: 'غير متصل', kind: 'neutral' },
];

export const AGENT_STATE_MAP = Object.fromEntries(
  AGENT_STATE_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<AgentState, Labelled<AgentState>>>;

/** ما يقلبه الموظف بيده — «في مكالمة» يقلبها النظام وحده. */
export const AGENT_STATES_MANUAL: readonly AgentState[] = [
  'AVAILABLE',
  'BUSY',
  'BREAK',
  'OFFLINE',
];

/* ==========================================================================
   تقييم الجودة
   ========================================================================== */

/**
 * معايير مراجعة المكالمة — من مئة درجة.
 *
 * الأوزان ثابتةٌ في الكتالوج لا في قاعدة البيانات: تغييرها يغيّر معنى
 * الدرجات القديمة، فتعديلها قرارُ إصدارٍ لا إعدادُ شاشة.
 */
export interface QaCriterion {
  key: string;
  label: string;
  weight: number;
}

export const QA_CRITERIA: readonly QaCriterion[] = [
  { key: 'opening', label: 'الافتتاحية', weight: 10 },
  { key: 'communication', label: 'التواصل والأسلوب', weight: 20 },
  { key: 'needs', label: 'فهم احتياج العميل', weight: 15 },
  { key: 'knowledge', label: 'المعرفة بالخدمة', weight: 15 },
  { key: 'objections', label: 'معالجة الاعتراضات', weight: 15 },
  { key: 'closing', label: 'الإغلاق', weight: 15 },
  { key: 'followup', label: 'ترتيب المتابعة', weight: 10 },
];

/** مجموع الأوزان — يُفحص في الاختبارات كي لا يكسره تعديل عابر. */
export const QA_TOTAL_WEIGHT = QA_CRITERIA.reduce((n, c) => n + c.weight, 0);

/**
 * الدرجة الكلية من درجات المعايير (كلٌّ من ١٠٠).
 * المفقود يُحسب صفرًا: معيارٌ لم يُقيَّم ليس معيارًا ممتازًا.
 */
export function qaTotal(scores: Record<string, number>): number {
  let total = 0;
  for (const c of QA_CRITERIA) {
    const v = scores[c.key];
    total += (Math.max(0, Math.min(100, typeof v === 'number' ? v : 0)) * c.weight) / 100;
  }
  return Math.round(total);
}

/* ==========================================================================
   مزوّد الاتصالات
   ========================================================================== */

/**
 * المزوّدون المعروفون للطبقة.
 *
 * `NONE` هو الصادق اليوم: لا نقل صوتيًّا عبر النظام، والاتصال من جهاز
 * الموظف ويُسجَّل هنا. والبقية أسماء محجوزة تُفعَّل حين يُربط حساب فعلي —
 * الشاشة لا تعرض زرًّا يوحي بعملها قبل ذلك.
 */
export const TELEPHONY_PROVIDERS = ['NONE', 'TWILIO', 'TELNYX', 'PLIVO', 'VONAGE'] as const;
export type TelephonyProviderKind = (typeof TELEPHONY_PROVIDERS)[number];

export const TELEPHONY_PROVIDER_DEFS: readonly Labelled<TelephonyProviderKind>[] = [
  { key: 'NONE', label: 'بلا مزوّد — الاتصال من جهاز الموظف', kind: 'neutral' },
  { key: 'TWILIO', label: 'Twilio', kind: 'info' },
  { key: 'TELNYX', label: 'Telnyx', kind: 'info' },
  { key: 'PLIVO', label: 'Plivo', kind: 'info' },
  { key: 'VONAGE', label: 'Vonage', kind: 'info' },
];

/* ==========================================================================
   قوالب الرسائل السريعة
   ========================================================================== */

/**
 * قناتان لا ثلاث — بصدق.
 *
 * البريد يخرج من صندوق الشركة عبر المنشئ، وواتساب يفتح المحادثة بالنص
 * جاهزًا على جهاز الموظف. وSMS غائبة عمدًا: لا بوابة رسائل نصية مربوطة،
 * وقناةٌ في القائمة لا ترسل شيئًا كذبٌ في واجهة.
 */
export const TEMPLATE_CHANNELS = ['EMAIL', 'SMS', 'WHATSAPP', 'MESSENGER', 'INSTAGRAM'] as const;
export type TemplateChannel = (typeof TEMPLATE_CHANNELS)[number];

export const TEMPLATE_CHANNEL_DEFS: readonly Labelled<TemplateChannel>[] = [
  { key: 'EMAIL', label: 'بريد إلكتروني', kind: 'info' },
  { key: 'SMS', label: 'رسالة نصية SMS', kind: 'neutral' },
  { key: 'WHATSAPP', label: 'واتساب', kind: 'success' },
  { key: 'MESSENGER', label: 'ماسنجر', kind: 'info' },
  { key: 'INSTAGRAM', label: 'إنستغرام', kind: 'danger' },
];

export const TEMPLATE_CHANNEL_MAP = Object.fromEntries(
  TEMPLATE_CHANNEL_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<TemplateChannel, Labelled<TemplateChannel>>>;

/** قنوات ميتا من قنوات القوالب — تُفتح داخل النظام لا في تطبيق خارجي. */
export const TEMPLATE_META_CHANNELS = ['WHATSAPP', 'MESSENGER', 'INSTAGRAM'] as const;

/**
 * ملء متغيّرات القالب.
 *
 * {{الاسم}} و{{name}} كلاهما يُقبل: من يكتب القالب عربيًّا لا يُجبر على
 * مفتاح لاتيني. والمجهول يُترك كما هو — قالبٌ ناقصُ متغيّرٍ يُقرأ خطؤه
 * في المعاينة خيرٌ من فراغٍ صامت.
 */
export function fillTemplate(
  body: string,
  vars: { name?: string | null; company?: string | null },
): string {
  return body
    .replaceAll('{{الاسم}}', vars.name ?? '')
    .replaceAll('{{name}}', vars.name ?? '')
    .replaceAll('{{الشركة}}', vars.company ?? '')
    .replaceAll('{{company}}', vars.company ?? '');
}

/* ==========================================================================
   متابعة العمل
   ========================================================================== */

/**
 * أوضاع مراقبة الشاشة.
 *
 * المتصفح يفرض الشفافية: الإذن يمنحه الموظف بيده كل جلسة ومؤشّر
 * المشاركة ظاهر طوالها — فالمراقبة الخفية مستحيلة تقنيًّا. وإعلام
 * الموظفين بالسياسة مسؤولية الإدارة.
 */
export const MONITOR_MODES = ['OFF', 'SCREENSHOTS', 'VIDEO'] as const;
export type MonitorMode = (typeof MONITOR_MODES)[number];

export const MONITOR_MODE_DEFS: readonly Labelled<MonitorMode>[] = [
  { key: 'OFF', label: 'بلا مراقبة', kind: 'neutral' },
  { key: 'SCREENSHOTS', label: 'لقطات شاشة دورية', kind: 'info' },
  { key: 'VIDEO', label: 'فيديو الوردية كاملة', kind: 'warning' },
];

/** حدود فاصل اللقطات بالثواني. */
export const SCREENSHOT_MIN_SEC = 10;
export const SCREENSHOT_MAX_SEC = 600;
